import { createSelector } from 'reselect'

import { resolveTasks, isDependant as isDependantUtil, testTaskDueDateFilter, filterTask } from '../utils/task';

const sortTasks = tasks => tasks.sort((t1, t2) => {
    const t1Order = (t1.order - 0);
    const t2Order = (t2.order - 0);
    return t1Order - t2Order;
});
const sortTasksByCompletionDate = tasks => tasks.sort((t1, t2) => new Date(t2.completed_on || new Date()).getTime() - new Date(t1.completed_on || new Date()).getTime());

const getTask = (tasks, id) => tasks.find(task => task.id === id || task.nice_id === id);
export const makeGetTask = () => createSelector(getTask, task => task);

const getSortedTasks = (tasks, ids) => sortTasks(tasks.filter(task => ids.includes(task.id) || ids.includes(task.nice_id)));
export const makeGetSortedTasks = () => createSelector(getSortedTasks, tasks => tasks);

const getProjectTasks = (tasks, projectId) => tasks.filter(task => task.project === projectId && !task.task);
export const makeGetProjectTasks = () => createSelector(getProjectTasks, tasks => tasks);

const getSortedActiveProjectTasks = (tasks, projectId) => tasks
    .filter(task => task.project === projectId && !task.archived && !task.task)
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1);
export const makeGetSortedActiveProjectTasks = () => createSelector(getSortedActiveProjectTasks, tasks => tasks);

const getSortedArchivedProjectTasks = (tasks, projectId, allowedTasks) => tasks
    .filter(task => {
        const passedFilter = task.project === projectId && task.archived && !task.task;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    })
    .sort((t1, t2) => new Date(t2.archived_on).getTime() - new Date(t1.archived_on).getTime());
export const makeGetSortedArchivedProjectTasks = () => createSelector(getSortedArchivedProjectTasks, tasks => tasks);

const getSortedCompletedProjectTasks = (tasks, projectId, allowedTasks) => tasks
    .filter(task => {
        const passedFilter = task.project === projectId && task.completed && !task.task && !task.archived;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    })
    .sort((t1, t2) => new Date(t2.completed_on).getTime() - new Date(t1.completed_on).getTime());
export const makeGetSortedCompletedProjectTasks = () => createSelector(getSortedCompletedProjectTasks, tasks => tasks);

const getMilestoneTasks = (tasks, milestoneId) => tasks.filter(task => task.milestone === milestoneId && !task.archived)
    .sort((t1, t2) => t1.name > t2.name ? 1 : -1)
    .sort((t1, t2) => new Date(t1.created_at) - new Date(t2.created_at))
    .sort((t1, t2) => (t1.due_date ? new Date(t1.due_date) : Number.POSITIVE_INFINITY) - (t2.due_date ? new Date(t2.due_date) : Number.POSITIVE_INFINITY));
export const makeGetMilestoneTasks = () => createSelector(getMilestoneTasks, tasks => tasks);

const getMilestoneActiveTasks = (tasks, milestoneId) => getMilestoneTasks(tasks, milestoneId).filter(task => !task.completed);
export const makeGetMilestoneActiveTasks = () => createSelector(getMilestoneActiveTasks, tasks => tasks);

const getProjectTasksWithMilestone = (tasks, projectId) => tasks.filter(task => task.project === projectId && task.milestone);
export const makeGetProjectTasksWithMilestone = () => createSelector(getProjectTasksWithMilestone, tasks => tasks);

export const makeGetFilteredTask = () => createSelector(filterTask, task => task);

const getActiveUserTasks = (tasks, userId) => tasks
    .filter(task => !task.archived && (task.assignees || []).includes(userId))
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1);
export const makeGetActiveUserTasks = () => createSelector(getActiveUserTasks, tasks => tasks);

const getSortedOpenProjectUserTasks = (tasks, projectId, userId) => tasks
    .filter(task => !task.archived && !task.completed && task.project === projectId && (task.assignees || []).includes(userId))
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1)
    .sort((p1, p2) => (p1.due_date ? new Date(p1.due_date) : Number.POSITIVE_INFINITY) - (p2.due_date ? new Date(p2.due_date) : Number.POSITIVE_INFINITY));
export const makeGetSortedOpenProjectUserTasks = () => createSelector(getSortedOpenProjectUserTasks, files => files);

const getOpenUserTasks = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.completed && (task.assignees || []).includes(userId))
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1);
export const makeGetOpenUserTasks = () => createSelector(getOpenUserTasks, tasks => tasks);

const getSortedOpenUserTasks = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.completed && !task.task && (task.assignees || []).includes(userId))
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1)
    .sort((p1, p2) => (p1.due_date ? new Date(p1.due_date) : Number.POSITIVE_INFINITY) - (p2.due_date ? new Date(p2.due_date) : Number.POSITIVE_INFINITY));

export const makeGetSortedOpenUserTasks = () => createSelector(getSortedOpenUserTasks, tasks => tasks);

const getSortedOpenUserTasksWithDueDate = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.completed && !task.task && task.due_date && (!userId || (task.assignees || []).includes(userId)))
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1)
    .sort((p1, p2) => (p1.due_date ? new Date(p1.due_date) : Number.POSITIVE_INFINITY) - (p2.due_date ? new Date(p2.due_date) : Number.POSITIVE_INFINITY));

export const makeGetSortedOpenUserTasksWithDueDate = () => createSelector(getSortedOpenUserTasksWithDueDate, tasks => tasks);

const getSortedCompletedUserTasksWithDueDate = (tasks, userId) => tasks
    .filter(task => !task.archived && task.completed && !task.task && task.due_date && (!userId || (task.assignees || []).includes(userId)))
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1)
    .sort((p1, p2) => (p1.due_date ? new Date(p1.due_date) : Number.POSITIVE_INFINITY) - (p2.due_date ? new Date(p2.due_date) : Number.POSITIVE_INFINITY));

export const makeGetSortedCompletedUserTasksWithDueDate = () => createSelector(getSortedCompletedUserTasksWithDueDate, tasks => tasks);

const getSortedOpenTasks = (tasks, projectIds) => tasks
    .filter(task => !task.archived && !task.completed && !task.task && (!projectIds || projectIds.includes(task.project)))
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1)
    .sort((p1, p2) => (p1.due_date ? new Date(p1.due_date) : Number.POSITIVE_INFINITY) - (p2.due_date ? new Date(p2.due_date) : Number.POSITIVE_INFINITY));

export const makeGetSortedOpenTasks = () => createSelector(getSortedOpenTasks, tasks => tasks);

const getOpenUserTasksCount = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.completed && (task.assignees || []).includes(userId)).length;
export const makeGetOpenUserTasksCount = () => createSelector(getOpenUserTasksCount, tasksCount => tasksCount);

const getCompletedUserTasksCount = (tasks, userId) => tasks
    .filter(task => !task.archived && task.completed && (task.assignees || []).includes(userId)).length;
export const makeGetCompletedUserTasksCount = () => createSelector(getCompletedUserTasksCount, tasksCount => tasksCount);

const getOverdueUserTasksCount = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.completed && task.due_date && (task.assignees || []).includes(userId) && new Date(task.due_date) < new Date()).length;
export const makeGetOverdueUserTasksCount = () => createSelector(getOverdueUserTasksCount, tasksCount => tasksCount);

const getTaskGroupTasks = (tasks, taskGroupId, projectId, allowedTasks) => sortTasks(tasks
    .filter(task => {
        const passedFilter = task.task_group === taskGroupId && !task.archived;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    }));
export const makeGetTaskGroupTasks = () => createSelector(getTaskGroupTasks, tasks => tasks);

const getTaskGroupOpenTasks = (tasks, taskGroupId, projectId, allowedTasks) => sortTasks(tasks
    .filter(task => {
        const passedFilter = task.task_group === taskGroupId && !task.archived && !task.completed;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    }));
export const makeGetTaskGroupOpenTasks = () => createSelector(getTaskGroupOpenTasks, tasks => tasks);

const getTransformedTaskGroupTasks = (tasks, taskGroupId, projectId, allowedTasks) => {
    const taskGroupTasks = getTaskGroupTasks(tasks, taskGroupId, projectId, allowedTasks);
    const transformed = {};

    taskGroupTasks.forEach((item, i) => {
        if (item) {
            transformed[i] = item;
        }
    });
    return transformed;
}
export const makeGetTransformedTaskGroupTasks = () => createSelector(getTransformedTaskGroupTasks, tasks => tasks);

const getTaskSubtasks = (tasks, taskId) => tasks.filter(task => task.task === taskId);
export const makeGetTaskSubtasks = () => createSelector(getTaskSubtasks, tasks => tasks);

const getTaskActiveSubtasks = (tasks, taskId) => sortTasks(tasks.filter(task => !task.completed && task.task === taskId));
export const makeGetTaskActiveSubtasks = () => createSelector(getTaskActiveSubtasks, tasksCount => tasksCount);

const getTaskCompletedSubtasks = (tasks, taskId) => sortTasksByCompletionDate(tasks.filter(task => task.completed && task.task === taskId));
export const makeGetTaskCompletedSubtasks = () => createSelector(getTaskCompletedSubtasks, tasksCount => tasksCount);

const getTaskSubtasksCount = (tasks, taskId) => tasks.filter(task => task.task === taskId).length;
export const makeGetTaskSubtasksCount = () => createSelector(getTaskSubtasksCount, tasksCount => tasksCount);

const getTaskCompletedSubtasksCount = (tasks, taskId) => tasks.filter(task => task.completed && task.task === taskId).length;
export const makeGetTaskCompletedSubtasksCount = () => createSelector(getTaskCompletedSubtasksCount, tasksCount => tasksCount);

const getProjectsFromTasks = tasks => [...new Set(tasks.map(task => task.project))];
export const makeGetProjectsFromTasks = () => createSelector(getProjectsFromTasks, projects => projects);

const getOpenTasks = tasks => tasks.filter(task => !task.archived && !task.completed);
export const makeGetOpenTasks = () => createSelector(getOpenTasks, projects => projects);

const getCompletedTasks = tasks => tasks.filter(task => !task.archived && task.completed);
export const makeGetCompletedTasks = () => createSelector(getCompletedTasks, projects => projects);

const getResolvedTasks = (taskIds, tasks) => {
    const resolvedTasks = resolveTasks(taskIds);

    if (Array.isArray(resolvedTasks)) {
        return resolvedTasks;
    }

    return [];
}
export const makeGetResolvedTasks = () => createSelector(getResolvedTasks, tasks => tasks);

const getResolvedOpenTasks = (taskIds, tasks) => getResolvedTasks(taskIds, tasks)
    .filter(task => !task.archived && !task.completed);
export const makeGetResolvedOpenTasks = () => createSelector(getResolvedOpenTasks, tasks => tasks);

const getResolvedCompletedTasks = (taskIds, tasks) => getResolvedTasks(taskIds, tasks)
    .filter(task => !task.archived && task.completed);
export const makeGetResolvedCompletedTasks = () => createSelector(getResolvedCompletedTasks, tasks => tasks);

const isDependant = (taskId, withDetails, tasks, milestones) => isDependantUtil(taskId, withDetails);
export const makeIsDependant = () => createSelector(isDependant, dependantOn => dependantOn);

const getTasksFetchedByGroups = (fetchInfo, taskGroupsIds) => taskGroupsIds.every(id => id === 'archived' || (fetchInfo.taskGroup[id] && fetchInfo.taskGroup[id].fetched));
export const makeGetTasksFetchedByGroups = () => createSelector(getTasksFetchedByGroups, fetched => fetched);

const getTasksFilteredByDueDate = (tasks, dueDateFilter) => tasks.filter(task => testTaskDueDateFilter(dueDateFilter, task));
export const makeGetTasksFilteredByDueDate = () => createSelector(getTasksFilteredByDueDate, tasks => tasks);
